import { css } from '@emotion/react';
import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import moment from 'moment';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { AngularContext } from 'react-app';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import {
  createMentoringProgramRecruitmentApplication,
  deleteMentoringProgramRecruitmentApplication,
  getCurrentMentoringProgramRecruitmentApplication,
  getMentoringProgramRecruitment,
} from 'redux/actions/mentoring-program-recruitments';
import { almostWhite } from 'athena/styles/colors';
import { white } from 'styles/global_defaults/colors';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import FlyerHeader from './flyer-header';
import FlyerInfo from './flyer-info';
import ExpressInterestSuccessModal from './express-interest-success-modal';


const styles = css`
  background-color: ${almostWhite};
  width: 100%;

  .flyer-container {
    background-color: ${white};
    max-width: 980px;
    height: fit-content;
    margin: 80px auto ${standardSpacing}px;
    border-radius: ${standardSpacing}px;
  }
`;

const Flyer = () => {
  const dispatch = useAppDispatch();

  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { programId, recruitmentId } = $state.params || {};

  const { isAdmin } = useMentoringProgramRole();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    name: programName,
    isEnrolled,
  } = mentoringProgram || {};

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [currentRecruitment, setCurrentRecruitment] = useState(null);

  const formattedInterestDeadline = currentRecruitment?.interestDeadline
    ? moment(currentRecruitment.interestDeadline).format('MMMM D, YYYY')
    : '';

  useEffect(() => {
    wrapThunkAction(dispatch(getCurrentMentoringProgramRecruitmentApplication({
      programId,
      recruitmentId,
    }))).then(({ payload }) => {
      setCurrentApplication(payload);
    });
  }, []);

  useEffect(() => {
    wrapThunkAction(dispatch(getMentoringProgramRecruitment({
      programId,
      recruitmentId,
    }))).then(({ payload }) => {
      setCurrentRecruitment(payload);
    });
  }, []);

  const handleClick = () => {
    if (isEnrolled) {
      $state.go('mentoring-program-root', { programId });
    } else if (currentApplication) {
      wrapThunkAction(dispatch(deleteMentoringProgramRecruitmentApplication({
        programId,
        recruitmentId,
      }))).then(() => {
        setCurrentApplication(null);
      });
    } else {
      wrapThunkAction(dispatch(createMentoringProgramRecruitmentApplication({
        programId,
        recruitmentId,
      }))).then(({ payload }) => {
        setCurrentApplication(payload);
        setShowSuccessModal(true);
      });
    }
  };

  let buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.EXPRESS();
  if (isEnrolled) {
    buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.ENTER();
  } else if (currentApplication) {
    buttonText = t.MENTORING_PROGRAMS.FLYER.BUTTON.WITHDRAW();
  }

  return (
    <div css={styles}>
      <div className='flyer-container'>
        <FlyerHeader
          roleAlias={currentRecruitment?.mentorshipProgram?.programAlias?.mentor?.downcasedPluralized ?? ''}
          programName={currentRecruitment?.mentorshipProgram?.name || ''}
          programDescription={currentRecruitment?.mentorshipProgram?.description || ''}
          releasedDate={currentRecruitment?.mentorshipProgram?.releasedDate}
          endDate={currentRecruitment?.mentorshipProgram?.endDate}
        />
        <div className='d-flex align-items-center justify-content-between p-5 border-top border-gray-6'>
          <div>
            <div className='heading-3'>{t.MENTORING_PROGRAMS.FLYER.READY_TO_MAKE_AN_IMPACT()}</div>
            <div className='text-small'>{t.MENTORING_PROGRAMS.FLYER.INTEREST_DEADLINE.DEFAULT(formattedInterestDeadline)}</div>
          </div>
          <Button
            variant='primary'
            onClick={handleClick}
            disabled={isAdmin}
          >
            {buttonText}
          </Button>
        </div>
        <FlyerInfo
          requirements={currentRecruitment?.requirements}
          includeRequirements={currentRecruitment?.includeRequirements}
          benefits={currentRecruitment?.benefits}
          includeBenefits={currentRecruitment?.includeBenefits}
          expectations={currentRecruitment?.expectations}
          includeExpectations={currentRecruitment?.includeExpectations}
        />
        {showSuccessModal && (
          <ExpressInterestSuccessModal
            onClose={() => setShowSuccessModal(false)}
            programName={currentRecruitment?.mentorshipProgram?.name || ''}
          />
        )}
      </div>
    </div>
  );
};

export default Flyer;
